// Public realtime-channel connection coordinates (ADR-0020). Only public
// `VITE_*` values — the endpoint host, the IoT custom authorizer's name, and the
// `{app}/{stage}` topic prefix the authorizer grants under. None of them grants
// anything on its own: a connection still requires a valid Cognito access token.
//
// `realtimeConfig` is `null` unless all three are present so the app FAILS
// CLOSED into today's behaviour — every consumer falls back to polling. Absent
// configuration is a supported mode (local Vite dev, or the SPA deployed ahead
// of the channel), never a crash.

export interface RealtimePublicConfig {
	/** IoT Core ATS endpoint host, without scheme or path. */
	endpoint: string
	/** Name of the IoT custom authorizer to invoke on connect. */
	authorizer: string
	/**
	 * `{app}/{stage}` namespace the authorizer grants topics under. Supplied
	 * pre-assembled rather than as two parts so the client cannot compose a prefix
	 * the authorizer would refuse.
	 */
	topicPrefix: string
}

const endpoint = import.meta.env.VITE_REALTIME_ENDPOINT
const authorizer = import.meta.env.VITE_REALTIME_AUTHORIZER
const topicPrefix = import.meta.env.VITE_REALTIME_TOPIC_PREFIX

/**
 * Whether a prefix is the `{app}/{stage}` shape the topic builder requires:
 * exactly one `/`, with a non-empty segment on each side.
 *
 * Checked here rather than only at connect time because a present-but-malformed
 * value must fail closed into polling like an absent one. The topic builder
 * throws on a bad prefix, and it is called from an effect with no catch — so
 * without this check a typo in the deployment's env would surface as a crash for
 * every signed-in user instead of a silently degraded channel.
 *
 * @param {string} value - The configured prefix.
 * @returns {boolean} True when the value can produce a valid topic filter.
 */
function isValidTopicPrefix(value: string): boolean {
	const separator = value.indexOf('/')
	return separator > 0 && separator === value.lastIndexOf('/') && separator < value.length - 1
}

// All three are required to build a topic filter the authorizer will accept, and
// the prefix must also be well-formed — presence alone is not enough.
export const realtimeConfig: RealtimePublicConfig | null =
	endpoint && authorizer && topicPrefix && isValidTopicPrefix(topicPrefix)
		? { endpoint, authorizer, topicPrefix }
		: null

export const isRealtimeConfigured = realtimeConfig !== null
